import { Box, Stack } from "@mui/material";
import FeedbackIllustration from "@src/appV2/redesign/assets/icons-colored/rating-prompt-feedback.svg?react";
import { Button } from "@src/appV2/redesign/components/Button";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type UseModalState } from "@src/lib/ui-react";

const FEEDBACK_MAX_CHARS = 1000;

export interface ReviewPromptFeedbackStepProps {
  modalState: UseModalState;
  title: string;
  body: string;
  placeholder: string;
  submitText: string;
  supportText: string;
  cancelText: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onContactSupport: () => void | Promise<void>;
  onDismiss: () => void | Promise<void>;
}

export function ReviewPromptFeedbackStep(props: ReviewPromptFeedbackStepProps) {
  const {
    modalState,
    title,
    body,
    placeholder,
    submitText,
    supportText,
    cancelText,
    value,
    onChange,
    onSubmit,
    onContactSupport,
    onDismiss,
  } = props;

  const isEmpty = value.trim().length === 0;

  return (
    <Dialog
      modalState={modalState}
      showCloseButton={false}
      width={360}
      onClose={async () => {
        await onDismiss();
      }}
    >
      <Stack alignItems="center" spacing={3}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            paddingTop: theme.spacing(2),
            "& > svg": {
              maxWidth: "160px",
              height: "auto",
            },
          })}
        >
          <FeedbackIllustration aria-hidden="true" />
        </Box>

        <Stack alignItems="center" spacing={1}>
          <Text
            semibold
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.base?.strong ?? theme.palette.text.primary,
              textAlign: "center",
              wordBreak: "break-word",
              textWrap: "balance",
            })}
          >
            {title}
          </Text>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.base?.base ?? theme.palette.text.secondary,
              textAlign: "center",
              wordBreak: "break-word",
            })}
          >
            {body}
          </Text>
        </Stack>

        <TextInput
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          placeholder={placeholder}
          value={value}
          inputProps={{ maxLength: FEEDBACK_MAX_CHARS, "aria-label": title }}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />

        <Stack spacing={2} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="primary"
            disabled={isEmpty}
            onClick={async () => {
              await onSubmit();
            }}
          >
            {submitText}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={async () => {
              await onContactSupport();
            }}
          >
            {supportText}
          </Button>
          <Button
            fullWidth
            variant="link"
            onClick={async () => {
              await onDismiss();
            }}
          >
            {cancelText}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
